import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import {
    DropdownMenuData,
    DropdownMenuItem } from '@vg-constellation/angular-16/dropdown';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { 
    maxLengths,
    regexPatterns,
    isInValidator} from "../shared/utilities/validators.utility";
import { Router } from "@angular/router";
import { ApiService } from "../shared/services/api.service";
import Cookies from "js-cookie";
import { ToastrService } from "ngx-toastr";
import { 
    GRADE_LEVEL_LIST,
    SIGNUP_EXTERNAL_LINKS, 
    COUNTRY_OPTIONS } from "../shared/config/app.constants";
import { map } from "rxjs";
import { AdobeLaunchService } from "../shared/services/adobe-launch.service";
import { UsStateData } from "../shared/models/us-state-data";
import { CanadianProvinceData } from "../shared/models/canadian-provinces-data";
import { SearchComponent } from "@vg-constellation/angular-16/search";
import { SchoolMetaData } from "../shared/models/school-meta-data";
import { TranslateService } from "@ngx-translate/core";
import { DigitalMaterialRequestAttribute } from "../models/material-interface";
import valZip from "val-zip";
import {
    HowDidYouHearAboutUsResponse,
    mapHowDidYouHearAboutUsOptions,
} from "../shared/utilities/how-did-you-hear-about-us.utility";

@Component({
    selector: 'app-materials-form',
    templateUrl: './materials-form.component.html',
    styleUrls: ['./materials-form.component.scss']
})
export class MaterialsFormComponent implements OnInit{
    public materialUrlPath: string;
    dataCollectionMessage: string;
    fileDetails;
    triggerEl;
    materialsForm: FormGroup;
    isCountryCanada = false;
    isSubmittedAndInvalid = false;
    isSaving = false;
    
    signupExternalLinks = SIGNUP_EXTERNAL_LINKS;
    maxLengths = maxLengths;
    countries = COUNTRY_OPTIONS;
    states = UsStateData;
    provinces = CanadianProvinceData;

    gradeLevelList: DropdownMenuData;
    gradeLevelItemList: DropdownMenuItem[] = [];
    references = {};

    selectedGradeLevelArray: [] = [];
    selectedGrades = '';

    // Utility
    preserveOriginalOrder = () => 0;  // Stops the keyvalue pipe from sorting results on the key``

    // Search school
    enableAddSchoolMode: boolean = false;
    schoolCountryRegionObj: Object = {};
    @ViewChild('searchComponent')
    searchComponent!: SearchComponent;
    selectedSchoolMetaData: SchoolMetaData | null = null;
    initialInputValue: string = '';
    selectedSchool: string = '';

    //School Search
    triggeredSchoolSearch: Boolean = false;
    schools = [];

    readonly maxEmailLength = maxLengths.email;
    readonly maxSchoolNameLength = maxLengths.schoolName;

    constructor(
        public _router: Router,
        private readonly fb: FormBuilder,
        private readonly _apiService: ApiService,
        public bsModalRef: BsModalRef,
        private readonly toast: ToastrService,
        private readonly adobeLaunchService: AdobeLaunchService,
        private readonly translateService: TranslateService,
        private elementRef: ElementRef
    ) {
        this.createGradeListDropdown();
        this.setDataCollectionMessage();
    }

    ngOnInit(): void {
        this.schoolCountryRegionObj = COUNTRY_OPTIONS;
        this.getHowDidYouHearAboutUs();
        this.initForm();
        this.bsModalRef.onHide.subscribe(_ => {
            this.triggerEl.focus({
                focusVisible:true
            });
        });

        //Adobe launch implementation
        this.adobeLaunchService.trackSubPage('modal_download_material_open');
    }

    ngAfterViewInit(): void {
        const dom: HTMLElement = this.elementRef.nativeElement;
        const dropDownTriggerElements = dom.querySelectorAll('.c11n-dropdown__trigger');
        let count = 0;

        dropDownTriggerElements.forEach(p => {
            if (count == 0) {
                p.setAttribute('aria-describedby','grade-level-description');
            } else if (count == 1) {
                p.setAttribute('aria-describedby','how-description');
            }
            count++;
        });
    }

    initForm(): void {
        this.materialsForm = this.fb.group({
            firstName: [
                '', 
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(maxLengths.firstName),
                    Validators.pattern(regexPatterns.name)
                ])
            ],
            lastName: [
                '', 
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(maxLengths.lastName),
                    Validators.pattern(regexPatterns.name)
                ])
            ],
            email: [
                '', 
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(maxLengths.email),
                    Validators.pattern(regexPatterns.email)
                ])
            ],
            schoolcountryregion: [this.countries.unitedStates, ''],
            schoolName: ['', ''],
            schoolstreetaddress:  ['', ''],
            schoolsecondaryaddressunit:  ['', ''],
            schoolcity:  ['', ''],
            schoolstate:  ['', ''],
            schoolMetaDataId: ['', Validators.required],
            schoolZipCode: ['', ''],
            schoolAddressFullText: ['', ''],
            gradeLevel: [
                '', 
                Validators.compose([
                    Validators.required
                ]),
            ],
            reference: [
                '', 
                Validators.compose([
                    Validators.required
                ]),
            ],
            conference: [
                ''
            ],
            partnership: [
                ''
            ],
            isTermsAndConditionsAccepted: [
                false, 
                Validators.compose([
                    Validators.requiredTrue
                ])
            ],
            isPrivacyPolicyAccepted: [
                false, 
                Validators.compose([
                    Validators.requiredTrue
                ])
            ],
            captchaResponse: [
                '',
                Validators.compose([
                    Validators.required
                ])
            ]
        });
    
        this.materialsForm.controls["schoolZipCode"].addValidators(this.isValidZip());
    }

    private setDataCollectionMessage(): void {
        this.dataCollectionMessage = `
                ${this.translateService.instant('informationRequest.digitalDownload')}
                <br>
                <br>
                ${this.translateService.instant('informationRequest.privacy_note')}
        `;
    }

    createGradeListDropdown() {
        Object.entries(GRADE_LEVEL_LIST).forEach(([key, value]) => {
          this.gradeLevelItemList.push({
            labelText: value,
            id: key
          });
        });
        this.gradeLevelList = {
          groups: [
            {
              items: this.gradeLevelItemList
            }
          ]
        };
    }

    getSelectedGrades(selectedItemData) {
        this.selectedGradeLevelArray = selectedItemData.activeItems;
        const filteredArray = [];
        this.selectedGradeLevelArray.forEach((checkedItem) => {
          filteredArray.push(GRADE_LEVEL_LIST[checkedItem]);
        });
        this.selectedGrades = filteredArray.toString();
        this.materialsForm?.controls['gradeLevel']?.setValue(
          this.selectedGradeLevelArray
        );
    }

    onSchoolSelect(school: SchoolMetaData): void {
        this.selectedSchoolMetaData = school;
        this.patchSchoolMetaId(school.schoolMetaDataId);
    }

    patchSchoolMetaId(schoolMetaId: string): void {
        this.materialsForm.patchValue({
            schoolMetaDataId: schoolMetaId
        });
    }

    onManualAdd(): void {
        this.enableAddSchoolMode = !this.enableAddSchoolMode;
        this.materialsForm.controls['schoolMetaDataId'].clearValidators();
        this.materialsForm.controls['schoolMetaDataId'].updateValueAndValidity();
        this.materialsForm.controls['schoolMetaDataId'].setValue('');
        this.addSchoolFormFieldsValidation();
    }

    returnToSearch(): void {
        this.enableAddSchoolMode = !this.enableAddSchoolMode;  
        this.materialsForm.controls['schoolMetaDataId'].setValidators(Validators.required);
        this.materialsForm.controls['schoolMetaDataId'].updateValueAndValidity();
        this.onSearchNewSchool();
        this.removeSchoolFormFieldsValidation();
    }

    onSearchNewSchool(): void {
        this.triggeredSchoolSearch = false;
        this.selectedSchoolMetaData = null
        this.patchSchoolMetaId('');
    }

    trackBySchoolId(_: number, school:SchoolMetaData): string {
        return school.schoolMetaDataId;
    }

    addSchoolFormFieldsValidation() {
        const schoolFieldsArray = ['schoolName', 'schoolstreetaddress', 'schoolsecondaryaddressunit', 'schoolcity', 'schoolstate'];
        schoolFieldsArray.forEach((formField) => {
          if(formField==='schoolName'){
            this.materialsForm.controls[formField].setValidators([
                Validators.required, 
                Validators.maxLength(this.maxLengths.schoolName),
                Validators.pattern(regexPatterns.name)
            ]);
          } else if (formField==='schoolstreetaddress') {
            this.materialsForm.controls[formField].setValidators([
                Validators.required, 
                Validators.maxLength(this.maxLengths.streetAddress),
                Validators.pattern(regexPatterns.name)
            ]);
          } else if (formField==='schoolsecondaryaddressunit') {
            this.materialsForm.controls[formField].setValidators([
                Validators.maxLength(this.maxLengths.secondaryAddressUnit),
                Validators.pattern(regexPatterns.name)
            ]);
          } else if (formField==='schoolcity') {
            this.materialsForm.controls[formField].setValidators([
                Validators.required, 
                Validators.maxLength(this.maxLengths.city),
                Validators.pattern(regexPatterns.name)
            ]);
          } else {
            this.materialsForm.controls[formField].setValidators([Validators.required]);
          }
          this.materialsForm.controls[formField].updateValueAndValidity();
        });
        this.materialsForm.controls['schoolZipCode'].setValidators(this.isValidZip());
        this.materialsForm.controls['schoolZipCode'].addValidators(Validators.required);
        this.materialsForm.controls['schoolZipCode'].updateValueAndValidity();       

        this.onSchoolAddressCountryChange({value: this.materialsForm.value.schoolcountryregion});
    }
    
    removeSchoolFormFieldsValidation() {
        const schoolFieldsArray = ['schoolName', 'schoolstreetaddress', 'schoolsecondaryaddressunit', 'schoolcity', 'schoolstate'];
        schoolFieldsArray.forEach((formField) => {
            this.materialsForm.controls[formField].clearValidators();
            this.materialsForm.controls[formField].updateValueAndValidity();
            this.materialsForm.controls[formField].setValue('');
        });
        this.materialsForm.controls['schoolZipCode'].clearValidators();
        this.materialsForm.controls['schoolZipCode'].updateValueAndValidity();
    }

    isValidZip(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
        
            if (value.length === 0) {
            return null
            }

            if (this.isCountryCanada) {
            return value.match(regexPatterns.zipCanada) ? null : { inValidZip: true };
            } else {
            const isValidUSZip = valZip(value, 'US') && value.match(regexPatterns.zipUsFormat);
            return isValidUSZip ? null : { inValidZip: true };
            }
        };
    }

    addOtherCountryFormFieldsValidation() {
        this.materialsForm.controls['schoolName'].setValidators([
            Validators.required, 
            Validators.maxLength(this.maxLengths.schoolName),
            Validators.pattern(regexPatterns.name)
        ]);
        this.materialsForm.controls['schoolName'].updateValueAndValidity();
        this.materialsForm.controls['schoolAddressFullText'].setValidators([
            Validators.required, 
            Validators.maxLength(this.maxLengths.streetAddress),
            Validators.pattern(regexPatterns.name)
        ]);
        this.materialsForm.controls['schoolAddressFullText'].updateValueAndValidity();

        this.materialsForm.controls['schoolMetaDataId'].clearValidators();
        this.materialsForm.controls['schoolMetaDataId'].updateValueAndValidity();
        this.materialsForm.controls['schoolMetaDataId'].setValue('');
    }

    removeOtherCountrySchoolAddressValidation() {
        this.materialsForm.controls['schoolAddressFullText'].clearValidators();
        this.materialsForm.controls['schoolAddressFullText'].updateValueAndValidity();
        this.materialsForm.controls['schoolAddressFullText'].setValue('');
    }

    onSearchAction(event) {
        switch (event.searchEvent) {
            case 'input': {
                this.triggeredSchoolSearch = false
                break;
            }
            case 'submit': {
                this._apiService.getSchoolMetaData(event.searchTerm).pipe(
                    map((res) => {
                    return res.schoolMetaDataList.map(school => ({
                        schoolMetaDataId: school.schoolMetaDataId,
                        schoolName: school.schoolName,
                        zipCode: school.zipCode
                    }));
                    })
                ).subscribe({
                    next: (schools) => {
                        this.schools = schools;
                        this.triggeredSchoolSearch = true;
                    },
                    error: (_) => {
                    }
                });
                break;
            }
            case 'clear': {
                this.materialsForm.controls['schoolName'].setValue('');
                this.selectedSchool = '';
                this.triggeredSchoolSearch = false;
            break;
            }
        }
    }

    onSchoolAddressCountryChange(event): void {
        const validators = [];
        this.isCountryCanada = event.value === this.countries.canada;

        if (event.label === this.countries.other) {
            if (this.enableAddSchoolMode) {
                this.enableAddSchoolMode = false;
            }
            this.removeSchoolFormFieldsValidation();
            this.addOtherCountryFormFieldsValidation();
        } else {
            this.removeOtherCountrySchoolAddressValidation();
            if (!this.enableAddSchoolMode) {
                this.materialsForm.controls['schoolMetaDataId'].setValidators(Validators.required);
                this.materialsForm.controls['schoolMetaDataId'].updateValueAndValidity();
            }
        }
    
        if (this.enableAddSchoolMode) {
            validators.push(Validators.required);

            if (event.label === this.countries.unitedStates) {
                validators.push(isInValidator(this.states.map(state => state.stateCode)));
                this.materialsForm.setControl('schoolstate', this.fb.control(
                    '',
                    Validators.compose(validators)
                ));
            } else if (event.label === this.countries.canada) {
                validators.push(isInValidator(this.provinces.map(province => province.canadianProvinceCode)));
                this.materialsForm.setControl('schoolstate', this.fb.control(
                    '',
                    Validators.compose(validators)
                ));
            }
        }
    }

    navigateAway() {
        //Adobe launch implementation
        this.adobeLaunchService.trackSubPage('modal_download_material_cancel');

        if (!this.materialUrlPath) {
            this._router.navigate(["/home"]);
        } 
        this.onModalClose();
    }

    showToastrMsg(
        header: string,
        description: string,
        type: string
    ) {
        return this.toast.show(description, header, { toastClass: type });
    }

    showErrorMsg(): void {
        this.showToastrMsg(
            'Request Failed', 
            'Please try again later. If the issue persists, please contact us at support@myclassroomeconomy.org', 
            'error'
        );
    }

    createDomesticRequestObject() {
        return {
            firstName: this.materialsForm.value.firstName,
            lastName: this.materialsForm.value.lastName,
            email: this.materialsForm.value.email,
            schoolMetaDataId: this.materialsForm.value.schoolMetaDataId,
            schoolName: this.selectedSchoolMetaData.schoolName,
            schoolZipCode: this.selectedSchoolMetaData.zipCode,
            schoolcountryregion: this.materialsForm.value.schoolcountryregion,
            gradeLevel: this.selectedGradeLevelArray,
            reference: this.materialsForm.value.reference,
            isTermsAndConditionsAccepted: this.materialsForm.value.isTermsAndConditionsAccepted,
            isPrivacyPolicyAccepted: this.materialsForm.value.isPrivacyPolicyAccepted,
            language: this.translateService.currentLang,
            captchaResponse: this.materialsForm.value.captchaResponse
        };
    }

    createOtherCountryRequestObject() {
        return {
            firstName: this.materialsForm.value.firstName,
            lastName: this.materialsForm.value.lastName,
            email: this.materialsForm.value.email,
            schoolName: this.materialsForm.value.schoolName,
            schoolAddressFullText: this.materialsForm.value.schoolAddressFullText,
            schoolcountryregion: this.materialsForm.value.schoolcountryregion,
            gradeLevel: this.selectedGradeLevelArray,
            reference: this.materialsForm.value.reference,
            isTermsAndConditionsAccepted: this.materialsForm.value.isTermsAndConditionsAccepted,
            isPrivacyPolicyAccepted: this.materialsForm.value.isPrivacyPolicyAccepted,
            language: this.translateService.currentLang,
            captchaResponse: this.materialsForm.value.captchaResponse
        }
    }

    onSubmit(): void {
        this.isSubmittedAndInvalid = this.materialsForm.invalid;
        if (!this.materialsForm.invalid) {
            this.isSaving = true;
            
            if(this.enableAddSchoolMode){
                const schoolObj = [{
                  schoolName: this.materialsForm.value.schoolName,
                  country: this.materialsForm.value.schoolcountryregion,
                  streetAddress1: this.materialsForm.value.schoolstreetaddress,
                  streetAddress2: this.materialsForm.value.schoolsecondaryaddressunit,
                  city: this.materialsForm.value.schoolcity,
                  state:this.materialsForm.value.schoolstate,
                  zipCode: this.materialsForm.value.schoolZipCode,
                }];
                this._apiService.createSchoolMetaData(schoolObj).subscribe({
                    next: (schoolObjResponse) => {
                        if (schoolObjResponse.error || schoolObjResponse.code) {
                            this.showErrorMsg();
                            this.isSaving = false;
                        } else if (schoolObjResponse && schoolObjResponse[0].schoolMetaDataId) {
                            this.materialsForm.patchValue({
                                schoolMetaDataId: schoolObjResponse[0].schoolMetaDataId
                            });
                            this.selectedSchoolMetaData = schoolObjResponse[0];
                            this.callCreateDigitalMaterial();
                        } else {
                            this.showErrorMsg();
                            this.isSaving = false;
                        }
                    },
                    error: (_) => {
                        this.showErrorMsg();
                        this.isSaving = false;
                    }
                });
            } else {
                this.callCreateDigitalMaterial();
            }
        }
    }

    callCreateDigitalMaterial() {
        let digitalMaterialRequestObject: DigitalMaterialRequestAttribute;
        if (this.materialsForm.value.schoolcountryregion === this.countries.other) {
            digitalMaterialRequestObject = this.createOtherCountryRequestObject();
        } else {
            digitalMaterialRequestObject = this.createDomesticRequestObject();
        }

        this._apiService.createDigitalMaterial(digitalMaterialRequestObject).subscribe({
            next: response => {
                if (response.error || response.code) {
                    this.showErrorMsg();
                    this.isSaving = false;
                } else {
                    this.isSaving = false;
                    Cookies.set('canDownloadMaterials', 'true'); // Session

                    //Adobe launch implementation
                    this.adobeLaunchService.trackSubPage('modal_download_material_complete');

                    this.bsModalRef.hide();                
                    if (this.materialUrlPath) {
                        //Adobe launch implementation
                        this.adobeLaunchService.contentDownloaded(this.fileDetails);

                        window.open(this.materialUrlPath, '_blank');
                    }
                }
            },
            error: () => {
                this.showErrorMsg();
                this.isSaving = false;
            }
        });
    }

    getHowDidYouHearAboutUs(): void {
        const locale = this.translateService.getCurrentLang() || this.translateService.getFallbackLang() || 'en';
        this._apiService.getHowDidYouHearAboutUs(locale).subscribe({
            next: (response: HowDidYouHearAboutUsResponse) => {
                this.references = mapHowDidYouHearAboutUsOptions(response);
            },
            error: (_) => {
                this.references = {};
            },
        });
    }
    
    onModalClose() {
        this.bsModalRef.hide();
    }
}
